// Ayuda → «Contactar a soporte» (CC-CONSOLE #1088): lógica pura del formulario
// que deja una duda, una sugerencia o un bug en la Mesa de Servicio.
//
// Reutiliza la creación de tickets que ya existe (POST /serviceDesk/tickets, el
// mismo núcleo que usa el portal público /soporte): aquí sólo se decide qué
// etiqueta y categoría lleva cada tipo, qué contexto se manda y cómo se arma el
// cuerpo. Sin React, para poder probarlo aislado.
import type { BrandOption, CategoryOption, LabelOption, TicketPriority } from "../../service-desk/services/serviceDeskService";
import type { Lang } from "../../../tutorials/utils";

export type SupportType = "question" | "suggestion" | "bug";

export const SUPPORT_TYPES: SupportType[] = ["question", "suggestion", "bug"];

interface SupportTypeMeta {
  label: Record<Lang, string>;
  hint: Record<Lang, string>;
  icon: string;
  /** Id de etiqueta en la configuración de la Mesa de Servicio (DEFAULT_LABELS). */
  labelId: string;
  /** Id de categoría en la configuración de la Mesa de Servicio (DEFAULT_CATEGORIES). */
  categoryId: string;
  priority: TicketPriority;
}

export const SUPPORT_TYPE_META: Record<SupportType, SupportTypeMeta> = {
  question: {
    label: { es: "Duda", en: "Question" },
    hint: { es: "Pregunta cómo hacer algo", en: "Ask how to do something" },
    icon: "ri-question-answer-line",
    labelId: "question",
    categoryId: "general",
    priority: "medium",
  },
  suggestion: {
    label: { es: "Sugerencia", en: "Suggestion" },
    hint: { es: "Propón una mejora o función", en: "Propose an improvement or feature" },
    icon: "ri-lightbulb-line",
    labelId: "feature_request",
    categoryId: "feature",
    priority: "low",
  },
  bug: {
    label: { es: "Reportar un bug", en: "Report a bug" },
    hint: { es: "Algo no funciona como debería", en: "Something isn't working as it should" },
    icon: "ri-bug-line",
    labelId: "bug",
    categoryId: "technical",
    priority: "medium",
  },
};

export const isSupportType = (v: unknown): v is SupportType =>
  typeof v === "string" && (SUPPORT_TYPES as string[]).includes(v);

/** Portal público: respaldo cuando la app no puede crear el ticket. */
export const PUBLIC_PORTAL_PATH = "/soporte";
/** Lista de tickets del usuario con sesión (respaldo del enlace de seguimiento). */
export const MY_TICKETS_PATH = "/mis-tickets";

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
/**
 * Tope de TODOS los adjuntos juntos: Cloud Functions rechaza cuerpos de más de
 * 32 MB con un 413 (y ya con el ticket a medias); 25 MB deja margen al resto
 * del formulario multipart.
 */
export const MAX_TOTAL_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/** Los mismos tipos que acepta la API (serviceDeskController ALLOWED_ATTACHMENT_TYPES). */
const ALLOWED_ATTACHMENT_TYPES = [
  "image/",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/zip",
  "application/json",
  "application/msword",
  "application/vnd.openxmlformats-officedocument",
];
export const ATTACHMENT_ACCEPT = "image/*,.pdf,.txt,.csv,.zip,.json,.doc,.docx,.xlsx,.pptx";

/** Tipo canónico por extensión para los archivos que el sistema reporta mal. */
const CANONICAL_TYPE_BY_EXT: Record<string, string> = {
  zip: "application/zip",
  csv: "text/csv",
};

/**
 * Windows reporta `application/x-zip-compressed` para un .zip y
 * `application/vnd.ms-excel` (o nada) para un .csv; la API sólo acepta los
 * tipos canónicos. Según la extensión, se re-crea el archivo con el tipo
 * correcto (mismo contenido, nombre y fecha). El resto se devuelve igual.
 */
export function normalizeAttachment(f: File): File {
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  const type = f.name.includes(".") ? CANONICAL_TYPE_BY_EXT[ext] : undefined;
  if (!type || f.type === type) return f;
  return new File([f], f.name, { type, lastModified: f.lastModified });
}

/**
 * Valida los adjuntos ANTES de enviar: la API crea el ticket y después sube los
 * archivos, así que un archivo rechazado dejaría un ticket creado con un error.
 * Devuelve el mensaje de error, o null si todo está bien.
 */
export function attachmentError(files: File[], lang: Lang): string | null {
  const tr = (es: string, en: string) => (lang === "en" ? en : es);
  if (files.length > MAX_ATTACHMENTS) {
    return tr(`Puedes adjuntar hasta ${MAX_ATTACHMENTS} archivos.`, `You can attach up to ${MAX_ATTACHMENTS} files.`);
  }
  for (const f of files) {
    if (!f.type || !ALLOWED_ATTACHMENT_TYPES.some((t) => f.type.startsWith(t))) {
      return tr(
        `«${f.name}» no es un tipo de archivo permitido (imágenes, PDF, texto, CSV, ZIP u Office).`,
        `“${f.name}” is not an allowed file type (images, PDF, text, CSV, ZIP or Office).`,
      );
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      return tr(`«${f.name}» pesa más de 10 MB.`, `“${f.name}” is larger than 10 MB.`);
    }
  }
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_TOTAL_ATTACHMENT_BYTES) {
    return tr(
      "Los archivos juntos pesan más de 25 MB. Quita alguno o compártelo comprimido.",
      "The files together are larger than 25 MB. Remove one or share it compressed.",
    );
  }
  return null;
}

/** Parámetros de la URL que nunca viajan en el ticket (tokens, llaves, correos…). */
const SENSITIVE_PARAM = /(token|key|secret|pass|pwd|auth|session|sig|signature|code|otp|jwt|credential|email|phone|ref)/i;

/**
 * Ruta de origen sin datos sensibles: pathname + query, quitando los
 * parámetros que parezcan credenciales o datos personales. Nunca el hash.
 */
export function sanitizeOrigin(pathname: string, search = ""): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const kept = new URLSearchParams();
  let removed = false;
  params.forEach((value, key) => {
    if (SENSITIVE_PARAM.test(key)) removed = true;
    else kept.append(key, value);
  });
  const q = kept.toString();
  return `${pathname || "/"}${q ? `?${q}` : ""}${removed ? " (parámetros sensibles omitidos)" : ""}`;
}

export interface SupportContext {
  name: string | null;
  email: string | null;
  companyName: string | null;
  companyId: string | null;
  origin: string;
  appVersion: string;
  userAgent: string;
  /** Contexto extra de quien abre el formulario (p. ej. la página de la wiki). */
  extra?: string | null;
}

/** Lo que se le muestra al usuario ANTES de enviar: exactamente lo que viaja. */
export function contextRows(ctx: SupportContext, lang: Lang): { label: string; value: string }[] {
  const tr = (es: string, en: string) => (lang === "en" ? en : es);
  const dash = "—";
  const company = [ctx.companyName, ctx.companyId ? `(${ctx.companyId})` : null].filter(Boolean).join(" ");
  const rows = [
    { label: tr("Nombre", "Name"), value: ctx.name || dash },
    { label: tr("Correo", "Email"), value: ctx.email || dash },
    { label: tr("Compañía", "Company"), value: company || dash },
    { label: tr("Pantalla", "Screen"), value: ctx.origin },
  ];
  if (ctx.extra) rows.push({ label: tr("Contexto", "Context"), value: ctx.extra });
  rows.push(
    { label: tr("Versión de la app", "App version"), value: ctx.appVersion },
    { label: tr("Navegador", "Browser"), value: ctx.userAgent || dash },
  );
  return rows;
}

/**
 * Cuerpo del ticket: el texto del usuario y, abajo, un bloque de contexto con
 * formato fijo (en español, para el equipo de la Mesa de Servicio). La API no
 * guarda compañía, ruta ni versión como campos, así que viajan aquí.
 */
export function buildDescription(type: SupportType, userText: string, ctx: SupportContext): string {
  const rows = contextRows(ctx, "es");
  const block = [
    "---",
    "Contexto enviado automáticamente desde la app (Ayuda → Contactar a soporte)",
    `Tipo: ${SUPPORT_TYPE_META[type].label.es}`,
    ...rows.map((r) => `${r.label}: ${r.value}`),
  ].join("\n");
  const text = userText.trim();
  return text ? `${text}\n\n${block}` : block;
}

/**
 * El parser multipart de la API (multipartParser.js) convierte en JSON
 * cualquier campo que empiece con «[» o «{», y en booleano los que son
 * exactamente «true» o «false» (→ 422 «debe ser texto»). Un espacio delante
 * los deja como texto (la API lo recorta).
 */
export const safeField = (v: string): string => (/^[[{]/.test(v) || /^(true|false)$/.test(v) ? ` ${v}` : v);

/** Marca de la Mesa de Servicio del tenant: sólo la propia, nunca la de otro. */
export function resolveBrand(themeId: string, brands: BrandOption[] | null | undefined): string {
  const id = (themeId || "").toLowerCase();
  if (!brands || !brands.length) return id;
  const hit =
    brands.find((b) => b.id.toLowerCase() === id) ||
    brands.find((b) => b.label.toLowerCase() === id) ||
    brands.find((b) => b.label.toLowerCase().replace(/[^a-z0-9]/g, "") === id.replace(/[^a-z0-9]/g, ""));
  return hit ? hit.id : id;
}

export interface SupportConfig {
  brands?: BrandOption[] | null;
  labels?: LabelOption[] | null;
  categories?: CategoryOption[] | null;
}

export interface SupportTicketPayload {
  brand: string;
  title: string;
  description: string;
  category: string;
  labels: string[];
  priority: TicketPriority;
  name?: string;
  email: string;
  files: File[];
}

/**
 * Payload para createMyTicket. Si la configuración de la Mesa de Servicio ya
 * cargó, sólo se usan la etiqueta y la categoría que existen allí (una
 * configuración editada no debe romper el envío).
 */
export function buildTicketPayload(input: {
  type: SupportType;
  title: string;
  description: string;
  ctx: SupportContext;
  themeId: string;
  config?: SupportConfig | null;
  files?: File[];
}): SupportTicketPayload {
  const meta = SUPPORT_TYPE_META[input.type];
  const cfg = input.config;
  const labelOk = !cfg?.labels?.length || cfg.labels.some((l) => l.id === meta.labelId);
  const categoryOk = !cfg?.categories?.length || cfg.categories.some((c) => c.id === meta.categoryId);
  return {
    brand: resolveBrand(input.themeId, cfg?.brands),
    title: safeField(input.title.trim()),
    description: safeField(buildDescription(input.type, input.description, input.ctx)),
    category: categoryOk ? meta.categoryId : "general",
    labels: labelOk ? [meta.labelId] : [],
    priority: meta.priority,
    name: input.ctx.name || undefined,
    email: input.ctx.email || "",
    files: input.files || [],
  };
}

/** Enlace de seguimiento: el mismo que da el portal público. */
export const followUpPath = (reference: string, token: string): string =>
  `${PUBLIC_PORTAL_PATH}/seguimiento?ref=${encodeURIComponent(reference)}&token=${encodeURIComponent(token)}`;

/** Mensaje de error de la API ({ meta: { message } } o { message }), si lo hay. */
export function apiErrorMessage(err: unknown): { status: number | null; message: string | null } {
  const e = err as { response?: { status?: number; data?: { meta?: { message?: unknown }; message?: unknown } } };
  const status = typeof e?.response?.status === "number" ? e.response.status : null;
  const raw = e?.response?.data?.meta?.message ?? e?.response?.data?.message;
  return { status, message: typeof raw === "string" && raw.trim() ? raw : null };
}
